import React from 'react';

import { Redirect, Route, Switch, useLocation } from 'react-router-dom';

import { useFeature } from '@growthbook/growthbook-react';
import { AuthenticatedRoute } from './routes/AuthenticatedRoute';

import { withModalState } from './components/modals/v2/withModalState';
import { DialogBase } from './components/modals/DialogBase';

import { useConsoleVersion } from './components/hooks/queries';
import { useAuth } from './components/hooks/useAuth';

// statics
import { PUBLIC_ROUTE_MAPPING } from './mappings/routes/routeMappings';
import { ConsoleResolver } from './console/ConsoleResolver';
import { CookieConsent } from './components/CookieConsent';
import { IncidentBanner } from './components/IncidentBanner';
import { getValidatedOAuthRedirect } from './components/entry/login/oauthRedirect.utils';
import { PERSONA_TYPES } from './console/_statics/persona.statics';

/**
 * @name RouteResolver
 * @description A layer of logic that is intended to re-route users to unauthenticated routes based on various conditions.
 */
export const RouteResolver = () => {
  const location = useLocation();

  const { accountBasePath, accountType, isAuthenticated } = useAuth();
  const incidentBannerFeature = useFeature('incident-banner');

  // get & set console version
  useConsoleVersion();

  const pendingOAuthRedirect =
    isAuthenticated && location.pathname === '/login'
      ? getValidatedOAuthRedirect()
      : null;

  React.useEffect(() => {
    if (!pendingOAuthRedirect) return;
    sessionStorage.removeItem('mcp_oauth_redirect');
    window.location.replace(pendingOAuthRedirect);
  }, [pendingOAuthRedirect]);

  if (isAuthenticated && location.pathname === '/login') {
    if (pendingOAuthRedirect) {
      return null;
    }
    return <Redirect to={loginLandingPath(accountType, accountBasePath)} />;
  }

  // redirect root routes to login
  if (location.pathname === '/') {
    return <Redirect to="/login" />;
  }

  return (
    <>
      <Switch>
        {PUBLIC_ROUTE_MAPPING.map((route) => (
          <Route key={`pr-${route.path}`} {...route} />
        ))}

        <AuthenticatedRoute path="/:persona" component={ConsoleResolver} />
      </Switch>

      <IncidentBanner show={incidentBannerFeature.on} />
      <CookieConsent />
      <ConsoleDialogs />
    </>
  );
};

const ConsoleDialogs = withModalState(DialogBase);

// A franchise has no dashboard; /admin opens its first allowed page.
const loginLandingPath = (
  accountType: string | null,
  accountBasePath: string | null
) =>
  accountType === PERSONA_TYPES.FRANCHISE
    ? `/${accountBasePath}`
    : `/${accountBasePath}/dashboard`;
